/**
 * Carrossel de produtos complementares (snippets/complementary-products-carousel.liquid).
 * O deslize é scroll nativo com scroll-snap; aqui ligamos setas (em loop), o contador «1/N»
 * e o autoplay opcional (`data-autoplay` = intervalo em ms).
 */
if (!customElements.get('complementary-carousel')) {
  class ComplementaryCarousel extends HTMLElement {
    constructor() {
      super();

      this.index = 0;
      this.frame = null;
      this.timer = null;
      this.pauseReasons = new Set();
      this.onScroll = this.onScroll.bind(this);
      this.onVisibilityChange = () => this.setPaused('hidden', document.hidden);
      this.stopAutoplay = this.stopAutoplay.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      this.slides = this.track ? Array.from(this.track.children) : [];
      this.counter = this.querySelector('[data-carousel-current]');

      if (this.slides.length < 2) return;

      this.querySelector('[data-carousel-prev]')?.addEventListener('click', () => {
        this.stopAutoplay();
        this.goTo(this.index - 1);
      });
      this.querySelector('[data-carousel-next]')?.addEventListener('click', () => {
        this.stopAutoplay();
        this.goTo(this.index + 1);
      });
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      const delay = Number(this.dataset.autoplay);
      if (delay > 0 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) this.startAutoplay(delay);
    }

    disconnectedCallback() {
      this.track?.removeEventListener('scroll', this.onScroll);
      if (this.frame) cancelAnimationFrame(this.frame);
      this.teardownAutoplay();
    }

    /**
     * @param {number} index - Índice desejado; valores fora do intervalo dão a volta.
     */
    goTo(index) {
      const total = this.slides.length;
      const target = this.slides[(index + total) % total];
      const offset = target.getBoundingClientRect().left - this.track.getBoundingClientRect().left;

      this.track.scrollBy({ left: offset });
    }

    onScroll() {
      if (this.frame) return;

      this.frame = requestAnimationFrame(() => {
        this.frame = null;

        const trackLeft = this.track.getBoundingClientRect().left;
        let closest = 0;
        let closestDistance = Infinity;

        for (const [i, slide] of this.slides.entries()) {
          const distance = Math.abs(slide.getBoundingClientRect().left - trackLeft);

          if (distance < closestDistance) {
            closest = i;
            closestDistance = distance;
          }
        }

        if (closest === this.index) return;

        this.index = closest;
        if (this.counter) this.counter.textContent = String(closest + 1);
      });
    }

    /**
     * Pausa com hover/foco, fora do ecrã ou separador oculto; para de vez quando o cliente usa setas ou desliza.
     * @param {number} delay - Intervalo entre slides em ms.
     */
    startAutoplay(delay) {
      this.delay = delay;
      this.autoplaying = true;
      this.pauseReasons.add('offscreen');
      // Anunciar cada troca automática seria ruído para leitores de ecrã.
      this.counter?.parentElement.setAttribute('aria-live', 'off');

      this.addEventListener('mouseenter', () => this.setPaused('hover', true));
      this.addEventListener('mouseleave', () => this.setPaused('hover', false));
      this.addEventListener('focusin', () => this.setPaused('focus', true));
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.setPaused('focus', false);
      });
      this.track.addEventListener('pointerdown', this.stopAutoplay, { once: true });
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      this.observer = new IntersectionObserver(([entry]) => this.setPaused('offscreen', !entry.isIntersecting));
      this.observer.observe(this);
    }

    /**
     * @param {string} reason - Motivo da pausa ('hover', 'focus', 'offscreen', 'hidden').
     * @param {boolean} paused
     */
    setPaused(reason, paused) {
      if (paused) this.pauseReasons.add(reason);
      else this.pauseReasons.delete(reason);
      this.scheduleNext();
    }

    scheduleNext() {
      clearTimeout(this.timer);
      if (!this.autoplaying || this.pauseReasons.size) return;

      this.timer = setTimeout(() => {
        this.goTo(this.index + 1);
        this.scheduleNext();
      }, this.delay);
    }

    stopAutoplay() {
      if (!this.autoplaying) return;

      this.teardownAutoplay();
      this.counter?.parentElement.setAttribute('aria-live', 'polite');
    }

    teardownAutoplay() {
      this.autoplaying = false;
      clearTimeout(this.timer);
      this.observer?.disconnect();
      this.track?.removeEventListener('pointerdown', this.stopAutoplay);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  customElements.define('complementary-carousel', ComplementaryCarousel);
}
